import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy, serverTimestamp, getDocsFromCache, getDocsFromServer } from 'firebase/firestore';
import { db, isUserAuthenticated } from './firebase';
import { Category } from '../types/category';

// Get flat collection references
const getCategoriesCollection = () => {
  if (!db) throw new Error('Firestore yüklenemedi');
  return collection(db, 'categories');
};

const getCategoryDocument = (id: string) => {
  if (!db) throw new Error('Firestore yüklenemedi');
  return doc(db, `categories/${id}`);
};

/**
 * Get all categories with cache-first strategy
 */
export const getAllCategories = async (): Promise<Category[]> => {
  try {
    // Check authentication first
    if (!isUserAuthenticated()) {
      throw new Error('User is not authenticated');
    }
    
    const q = query(
      getCategoriesCollection(),
      orderBy('createdAt', 'desc')
    );
    
    let snapshot;
    
    try {
      // Try to get from cache first
      snapshot = await getDocsFromCache(q);
      
      if (snapshot.empty && snapshot.metadata.fromCache) {
        snapshot = await getDocsFromServer(q);
      }
    } catch (cacheError) {
      console.warn('Could not fetch from cache, falling back to server:', cacheError);
      snapshot = await getDocsFromServer(q);
    }
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Category));
  } catch (error) {
    console.error('Categories could not be fetched:', error);
    throw error;
  }
};

/**
 * Get category by ID
 */
export const getCategoryById = async (id: string): Promise<Category | null> => {
  try {
    const docRef = getCategoryDocument(id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      } as Category;
    }
    
    return null;
  } catch (error) {
    console.error('Category could not be fetched:', error);
    throw error;
  }
};

/**
 * Add new category (Admin only)
 */
export const addCategory = async (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<Category> => {
  try {
    const categoryToAdd = {
      ...category,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(getCategoriesCollection(), categoryToAdd);
    
    return {
      id: docRef.id,
      ...category,
      createdAt: new Date(),
      updatedAt: new Date()
    } as Category;
  } catch (error) {
    console.error('Category could not be added:', error);
    throw error;
  }
};

/**
 * Update category (Admin only)
 */
export const updateCategory = async (id: string, category: Partial<Omit<Category, 'id' | 'createdAt'>>): Promise<void> => {
  try {
    const existingCategory = await getCategoryById(id);
    if (!existingCategory) {
      throw new Error('Category not found');
    }
    
    const docRef = getCategoryDocument(id);
    await updateDoc(docRef, {
      ...category,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Category could not be updated:', error);
    throw error;
  }
};

/**
 * Delete category (Admin only)
 */
export const deleteCategory = async (id: string): Promise<void> => {
  try {
    const existingCategory = await getCategoryById(id);
    if (!existingCategory) {
      throw new Error('Category not found');
    }
    
    const docRef = getCategoryDocument(id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Category could not be deleted:', error);
    throw error;
  }
}; 